// client/src/components/SetupPage.js
// First-run setup page — only reachable when no local users exist.
// Creates the initial admin account and logs straight in.

import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InterCollectIcon from '../Icons/InterCollect.png';

function SetupPage() {
  const [username, setUsername]         = useState('');
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [confirmPassword, setConfirm]   = useState('');
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [checking, setChecking]         = useState(true);

  // If setup is already done (explicit false), bounce to login.
  // Any other result (true, error, unexpected format) keeps us on this page —
  // the submit handler will surface the 403 if setup really isn't needed.
  useEffect(() => {
    axiosInstance.get('/auth/setup-required')
      .then(res => {
        if (res.data.required === false) window.location.href = '/login';
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post('/auth/setup', { username, email, password });
      const { token, isAdmin } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="xs">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <img src={InterCollectIcon} alt="InterCollect" style={{ width: 64, height: 64 }} />

          <Typography variant="h5" fontWeight={700}>Welcome to InterCollect</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No accounts exist yet. Create your admin account to get started.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
              fullWidth
              autoFocus
              inputProps={{ minLength: 3 }}
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              fullWidth
              inputProps={{ minLength: 10 }}
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirm(e.target.value)}
              required
              fullWidth
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AdminPanelSettingsIcon />}
            >
              {loading ? 'Creating account…' : 'Create Admin Account'}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default SetupPage;
